import type { AdminSelf } from '@saay/api-client';
import { IconTile, StatusBadge } from '@saay/blocks';
import { buttonVariants } from '@saay/shadcn/components/button';
import { SparklesIcon } from 'lucide-react';
import { formatCount, formatTimestamp, formatToman, statusLabel } from '@saay/formatting';
import {
  AccordionPanel,
  Banner,
  Button,
  CapacityMeter,
  Dialog,
  EmptyState,
  ErrorState,
  FactList,
  Icon,
  IconButton,
  LoadMore,
  Ltr,
  Menu,
  MenuItem,
  SheetSection,
  Skeleton,
  StatusChip,
  Table,
  useToast,
} from '../kit.js';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useOutletContext, useParams } from 'react-router';

import type {
  CreditProgram,
  CreditProgramActivation,
  CreditProgramClosure,
  CreditProgramRevisionCreate,
  TargetingRun,
} from '../api.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { formatProgramCodeRevision, formatRevisionLabel } from '../display.js';
import { creditProgramTargetingPath } from '../nav.js';
import { targetText } from '../components/targeting-snapshot.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { OPENED_FROM_LIST, onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import { approvalSubmitLabel, canUse } from '../permissions.js';
import {
  listStatus,
  programPath,
  useAdminSelf,
  useActiveOrganizationRoles,
  useActiveOrganizations,
  useCreditProgram,
  useCreditPrograms,
  useTargetingRuns,
} from '../queries.js';
import {
  CreditProgramDialog,
  type CreditProgramsOutletContext,
  programGuidance,
  programTitle,
  useRefreshPrograms,
  validityText,
} from './credit-programs.js';
import { TargetingRunSheet } from './targeting.js';

export { TargetingRunSheet };

export const CREDIT_PROGRAM_AUTO_EDIT = 'creditProgramAutoEdit';

/**
 * `/credit-programs/:programId`: one revision (AC §18.9; ERD §7.3: one row per revision) with its
 * terms, exposure, and allocation counts; the commands of its status (C4 edit, C5 activation 200/202,
 * C6 new revision, C11 closure); and its targeting runs (C9). `…/runs/:runId` is the run (C10).
 */

type ProgramAction = 'edit' | 'activate' | 'revise' | 'close';

function ProgramActionDialog({
  action,
  onOpenChange,
  program,
}: {
  readonly action: Exclude<ProgramAction, 'edit'>;
  readonly onOpenChange: (open: boolean) => void;
  readonly program: CreditProgram;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshPrograms(program.id);
  const self = useAdminSelf().data;
  const copy: Record<
    Exclude<ProgramAction, 'edit'>,
    { description: string; label: string; path: string; variant: 'primary' | 'danger' }
  > = {
    activate: {
      description:
        'فعال‌سازی نسخهٔ فعال قبلی همین کد را جایگزین می‌کند؛ تخصیص‌های آن دست نمی‌خورند. مدیر مالی و مدیر ارشد آن را بلافاصله اجرا می‌کنند؛ در غیر این صورت، اگر سقف تعهد از آستانه بیشتر باشد به تأیید مدیر دیگری می‌رود.',
      label: 'فعال‌سازی برنامه',
      path: '/activation',
      variant: 'primary',
    },
    close: {
      description:
        'بستن برای همین کد نهایی است: هدف‌گذاری و تخصیص جدید ممکن نیست و اجرای در انتظار تأیید ناموفق می‌شود؛ اعتبارهای داده‌شده تا انقضا قابل استفاده می‌مانند.',
      label: 'بستن برنامه',
      path: '/closure',
      variant: 'danger',
    },
    revise: {
      description: 'نسخهٔ جدید شرایط نسخهٔ فعال را کپی می‌کند و به‌صورت پیش‌نویس ویرایش می‌شود.',
      label: 'ساخت نسخهٔ جدید',
      path: '/revisions',
      variant: 'primary',
    },
  };
  const entry = copy[action];
  return (
    <ReasonDialog<CreditProgram>
      description={entry.description}
      eyebrow={programTitle(program)}
      guidance={programGuidance}
      name={`credit-program.${action}`}
      onDone={async (result, approval) => {
        await refresh();
        if (approval !== undefined) return;
        if (action === 'revise') {
          toast.success({ confirmedBy: result, title: 'نسخهٔ جدید به‌صورت پیش‌نویس ساخته شد' });
          void navigate(`/credit-programs/${result.data.id}`, {
            state: { ...OPENED_FROM_LIST, [CREDIT_PROGRAM_AUTO_EDIT]: true },
          });
          return;
        }
        toast.success({
          confirmedBy: result,
          title: `وضعیت برنامه: ${statusLabel('creditProgramStatus', result.data.status)}`,
        });
        onOpenChange(false);
      }}
      onError={async () => refresh()}
      onOpenChange={onOpenChange}
      send={(client, reason, intent) =>
        client.post(programPath(program.id, entry.path), {
          body: {
            expectedVersion: program.version,
            reason,
          } satisfies CreditProgramActivation & CreditProgramRevisionCreate & CreditProgramClosure,
          intent,
        })
      }
      submitLabel={action === 'activate' ? approvalSubmitLabel(self ?? {}) : entry.label}
      title={entry.label}
      variant={entry.variant}
    />
  );
}

function actionsFor(self: AdminSelf, program: CreditProgram): readonly ProgramAction[] {
  if (!canUse(self, 'updateCreditProgram')) return [];
  switch (program.status) {
    case 'DRAFT':
      return ['edit', 'activate'];
    case 'ACTIVE':
      return ['revise', 'close'];
    default:
      return [];
  }
}

const ACTION_LABELS: Readonly<Record<ProgramAction, string>> = {
  activate: 'فعال‌سازی',
  close: 'بستن برنامه',
  edit: 'ویرایش پیش‌نویس',
  revise: 'نسخهٔ جدید',
};

function RevisionHistorySection({ program }: { readonly program: CreditProgram }) {
  const siblings = useCreditPrograms({ programCode: program.programCode });
  const rows = (siblings.data?.pages.flatMap((page) => page.items) ?? [])
    .filter((row) => row.id !== program.id)
    .sort((left, right) => right.revision - left.revision);
  if (rows.length === 0) {
    return null;
  }
  return (
    <SheetSection id="program-revisions" title="نسخه‌های قبلی">
      <Table
        caption="نسخه‌های قبلی همین کد برنامه"
        columns={[
          {
            header: 'نسخه',
            key: 'revision',
            render: (row) => (
              <Link state={OPENED_FROM_LIST} to={`/credit-programs/${row.id}`}>
                {formatRevisionLabel(row.revision)}
              </Link>
            ),
          },
          {
            header: 'وضعیت',
            key: 'status',
            render: (row) => <StatusChip enumName="creditProgramStatus" value={row.status} />,
          },
          {
            header: 'فعال‌سازی',
            key: 'activated',
            render: (row) => (row.activatedAt === null ? '—' : formatTimestamp(row.activatedAt)),
          },
        ]}
        rowKey={(row) => row.id}
        rows={rows}
      />
    </SheetSection>
  );
}

function RunsSection({ program }: { readonly program: CreditProgram }) {
  const runs = useTargetingRuns(program.id);
  const rows = runs.data?.pages.flatMap((page) => page.items) ?? [];
  const organizations = useActiveOrganizations(rows.length > 0);
  const roles = useActiveOrganizationRoles(rows.length > 0);
  const open = (run: TargetingRun) => `/credit-programs/${program.id}/runs/${run.id}`;
  return (
    <SheetSection id="program-runs" title="نتایج تخصیص">
      <QueryState
        empty={<EmptyState icon="share" title="هنوز هدف‌گذاری روی این نسخه اجرا نشده است" />}
        error={runs.error}
        isEmpty={rows.length === 0}
        onRetry={() => void runs.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری اجراها…" lines={3} />}
        status={listStatus(runs)}
      >
        <Table
          caption="اجراهای هدف‌گذاری"
          columns={[
            {
              header: 'هدف',
              key: 'target',
              pin: 'start',
              render: (row) => (
                <Link state={OPENED_FROM_LIST} to={open(row)}>
                  {targetText(row.snapshot.target, {
                    organizationName: (id) =>
                      organizations.data?.find((item) => item.id === id)?.displayNameFa,
                    roleName: (id) => roles.data?.find((item) => item.id === id)?.nameFa,
                  })}
                </Link>
              ),
            },
            {
              header: 'موفق / ردشده',
              key: 'counts',
              numeric: true,
              render: (row) =>
                row.recipientCount === null
                  ? `${formatCount(row.snapshot.recipientCount)} برآورد`
                  : `${formatCount(row.recipientCount)} / ${formatCount(row.excludedCount ?? 0)}`,
            },
            {
              header: 'مبلغ نهایی',
              key: 'total',
              numeric: true,
              render: (row) =>
                row.allocatedTotalIrr === null
                  ? `${formatToman(row.snapshot.totalCommitmentIrr)} برآورد`
                  : formatToman(row.allocatedTotalIrr),
            },
            {
              header: 'زمان',
              key: 'created',
              render: (row) => formatTimestamp(row.executedAt ?? row.createdAt),
            },
            {
              header: 'وضعیت',
              key: 'status',
              pin: 'end',
              render: (row) => <StatusChip enumName="targetingRunStatus" value={row.status} />,
            },
          ]}
          rowKey={(row) => row.id}
          rows={rows}
        />
        {runs.isFetchNextPageError ? (
          <ErrorState error={runs.error} onRetry={() => void runs.fetchNextPage()} />
        ) : null}
        <LoadMore
          hasMore={runs.hasNextPage}
          loading={runs.isFetchingNextPage}
          onLoadMore={() => void runs.fetchNextPage()}
        />
      </QueryState>
    </SheetSection>
  );
}

function ProgramView({
  autoEdit,
  program,
  self,
}: {
  readonly autoEdit: boolean;
  readonly program: CreditProgram;
  readonly self: AdminSelf;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState<ProgramAction | null>(null);
  useEffect(() => {
    if (!autoEdit || program.status !== 'DRAFT') {
      return;
    }
    setOpen('edit');
    void navigate(location.pathname, { replace: true, state: OPENED_FROM_LIST });
  }, [autoEdit, location.pathname, navigate, program.status]);
  const actions = actionsFor(self, program);
  const close = (next: boolean) => {
    if (!next) setOpen(null);
  };
  const capRate =
    program.totalExposureCapIrr === 0
      ? '0'
      : String(program.exposure.committedIrr / program.totalExposureCapIrr);
  const termItems = [
    { key: 'source', label: 'منبع تأمین', value: 'SAAY · سرمایهٔ SAAY' },
    {
      key: 'organization',
      label: 'سازمان اختصاصی',
      value:
        program.dedicatedOrganization === null ? (
          'برنامهٔ SAAY برای همه'
        ) : (
          <Link to={`/organizations/${program.dedicatedOrganization.id}`}>
            {program.dedicatedOrganization.displayNameFa}
          </Link>
        ),
    },
    { key: 'validity', label: 'اعتبار زمانی', value: validityText(program.validityPolicy) },
    {
      key: 'allocations',
      label: 'تخصیص‌ها',
      value: `${formatCount(program.allocationCounts.active)} فعال · ${formatCount(program.allocationCounts.scheduled)} زمان‌بندی‌شده · ${formatCount(program.allocationCounts.expired)} منقضی · ${formatCount(program.allocationCounts.revoked)} لغوشده`,
    },
    {
      key: 'activated',
      label: 'فعال‌سازی',
      value: program.activatedAt === null ? null : formatTimestamp(program.activatedAt),
    },
    {
      key: 'supersedes',
      label: 'جایگزین نسخهٔ',
      value:
        program.supersedesProgramId === null ? null : (
          <Link to={`/credit-programs/${program.supersedesProgramId}`}>نسخهٔ قبلی</Link>
        ),
    },
    {
      key: 'by',
      label: 'ثبت‌کننده',
      value: `${program.createdBy?.displayNameFa ?? 'سیستم'} · ${formatTimestamp(program.createdAt)}`,
    },
    ...(program.reason === null
      ? []
      : [{ key: 'reason', label: 'دلیل', value: program.reason as ReactNode }]),
  ].filter((item) => item.value !== null && item.value !== '—');
  const canTarget = program.status === 'ACTIVE' && canUse(self, 'previewCreditProgramTargeting');
  const menuActions = actions.filter((action) => action === 'close');
  const barActions = actions.filter((action) => action !== 'close');
  return (
    <>
      <div className="admin-program-header">
        <div className="admin-program-header__title">
          <IconTile icon={SparklesIcon} tone="gold" />
          <h2 className="admin-program-header__name font-bold">{program.nameFa}</h2>
          <StatusChip title={formatRevisionLabel(program.revision)} />
          <StatusChip enumName="creditProgramStatus" value={program.status} />
          {program.status === 'SUPERSEDED' && program.supersededBy !== null ? (
            <StatusBadge tone="forest">
              <Link
                state={OPENED_FROM_LIST}
                to={`/credit-programs/${program.supersededBy.programId}`}
              >
                {`جایگزین‌شده با نسخهٔ ${formatRevisionLabel(program.supersededBy.revision)} ›`}
              </Link>
            </StatusBadge>
          ) : null}
        </div>
        <div className="admin-actions">
          {canTarget ? (
            <Link
              className={buttonVariants({ className: 'rounded-full px-5' })}
              state={OPENED_FROM_LIST}
              to={creditProgramTargetingPath(program.id)}
            >
              <Icon name="users" />
              تخصیص اعتبار
            </Link>
          ) : null}
          {barActions.map((action) => (
            <Button
              key={action}
              onClick={() => setOpen(action)}
              variant={action === 'activate' ? 'primary' : 'secondary'}
            >
              {ACTION_LABELS[action]}
            </Button>
          ))}
          {menuActions.length === 0 ? null : (
            <Menu
              align="end"
              label="کارهای بیشتر برنامه"
              trigger={<IconButton icon="more" label="کارهای بیشتر برنامه" variant="standard" />}
            >
              {menuActions.map((action) => (
                <MenuItem destructive key={action} onSelect={() => setOpen(action)}>
                  {ACTION_LABELS[action]}
                </MenuItem>
              ))}
            </Menu>
          )}
        </div>
      </div>
      {program.pendingApproval === null ? null : (
        <Banner tone="info">
          {`${statusLabel('approvalActionType', program.pendingApproval.actionType)} در انتظار تأیید است. `}
          <Link to={`/approvals/${program.pendingApproval.approvalRequestId}`}>
            مشاهدهٔ درخواست
          </Link>
        </Banner>
      )}
      <CapacityMeter
        label="تعهد برنامه نسبت به سقف"
        rate={capRate}
        valueText={`تعهدشده ${formatToman(program.exposure.committedIrr)} از ${formatToman(program.totalExposureCapIrr)}`}
      />
      <p className="admin-muted">
        {`مبلغ پیش‌فرض هر نفر ${formatToman(program.defaultAmountIrr)} · قابل تخصیص ${formatToman(program.exposure.availableUnderCapIrr)}`}
      </p>
      <AccordionPanel
        items={[
          {
            content: <FactList items={termItems} />,
            key: 'terms',
            title: 'شرایط نسخه',
          },
        ]}
        label="شرایط نسخه برنامه"
      />
      <RevisionHistorySection program={program} />
      <RunsSection program={program} />
      {open === 'edit' ? <CreditProgramDialog editing={program} onOpenChange={close} /> : null}
      {open === null || open === 'edit' ? null : (
        <ProgramActionDialog action={open} onOpenChange={close} program={program} />
      )}
    </>
  );
}

export function CreditProgramDetailSheet() {
  const { self } = useOutletContext<CreditProgramsOutletContext>();
  const { programId = '' } = useParams();
  const location = useLocation();
  const program = useCreditProgram(programId);
  const close = useCloseSheet('/credit-programs');
  const title = program.data === undefined ? 'برنامه اعتبار' : program.data.nameFa;
  const autoEdit =
    (location.state as Record<string, unknown> | null)?.[CREDIT_PROGRAM_AUTO_EDIT] === true;
  usePageTitle(title);
  return (
    <Dialog
      eyebrow={
        program.data === undefined ? (
          'برنامه اعتبار'
        ) : (
          <Ltr>{formatProgramCodeRevision(program.data.programCode, program.data.revision)}</Ltr>
        )
      }
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wide"
      title={title}
    >
      <QueryState
        error={program.error}
        onRetry={() => void program.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری برنامه…" lines={8} />}
        status={program.status}
      >
        {program.data === undefined ? null : (
          <ProgramView autoEdit={autoEdit} program={program.data} self={self} />
        )}
      </QueryState>
    </Dialog>
  );
}
